// File: frontend/src/pages/EntitiesIndexPage.tsx
// Threat Entities index: discovery entry point for entity profile pages.
// Lists canonical entities from the Knowledge Graph ranked by activity,
// with class filtering and name/alias search.
import React, { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
    Box,
    Container,
    Paper,
    Typography,
    Chip,
    Stack,
    Alert,
    CircularProgress,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    ToggleButton,
    ToggleButtonGroup,
    Link,
    alpha,
    Fade,
    styled,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import { useApi } from '../hooks/useApi';
import { cyber } from '../theme';
import { ENTITY_TYPE_COLORS } from './EntityProfilePage';

const HeroSection = styled(Box)(({ theme }) => ({
    background: cyber.gradient,
    border: `1px solid ${alpha(cyber.primaryLight, 0.35)}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    color: 'white',
}));

interface EntityIndexItem {
    id: string;
    name: string;
    type: string;
    aliases: string[] | null;
    external_ids: string[] | null;
    report_count: number;
    last_seen: string | null;
}

interface EntityIndexResponse {
    total: number;
    items: EntityIndexItem[];
}

interface SearchResponse {
    results: EntityIndexItem[];
}

const CLASSES = ['ThreatActor', 'Malware', 'Tool', 'Campaign'];
const PAGE_SIZE = 50;

// Backend /api/kg/entities orders both keys descending (most reported /
// most recently seen first), so the sort labels are one-directional.
type SortKey = 'report_count' | 'last_seen';

const EntityRow: React.FC<{ entity: EntityIndexItem; sortBy: SortKey }> = ({ entity, sortBy }) => {
    const color = ENTITY_TYPE_COLORS[entity.type] || '#9fa4c0';
    return (
        <TableRow hover>
            <TableCell>
                <Link
                    component={RouterLink}
                    to={`/entity/${entity.id}`}
                    sx={{ fontWeight: 'bold', color: 'text.primary', textDecorationColor: alpha(cyber.accent.cyan, 0.4), '&:hover': { color: cyber.accent.cyan } }}
                >
                    {entity.name}
                </Link>
                {entity.external_ids && entity.external_ids.length > 0 && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, fontFamily: cyber.fontMono, color: 'text.secondary' }}>
                        {entity.external_ids.slice(0, 2).join(' · ')}
                    </Typography>
                )}
            </TableCell>
            <TableCell>
                <Chip label={entity.type} size="small" sx={{ height: 20, fontSize: '0.68rem', color, bgcolor: alpha(color, 0.12), border: `1px solid ${alpha(color, 0.3)}`, fontWeight: 'bold' }} />
            </TableCell>
            <TableCell>
                {Array.from(
                    new Map(
                        (entity.aliases || [])
                            .filter((a) => a.toLowerCase() !== entity.name.toLowerCase())
                            .map((a) => [a.toLowerCase(), a])
                    ).values()
                ).slice(0, 3).map((a) => (
                    <Chip key={a} label={a} size="small" variant="outlined" sx={{ mr: 0.5, height: 18, fontSize: '0.65rem' }} />
                ))}
            </TableCell>
            <TableCell align="right">
                <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, color: sortBy === 'report_count' ? cyber.accent.cyan : 'text.secondary', fontWeight: 'bold' }}>
                    {entity.report_count}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <Typography variant="caption" sx={{ fontFamily: cyber.fontMono, color: sortBy === 'last_seen' ? cyber.accent.cyan : 'text.secondary', fontWeight: sortBy === 'last_seen' ? 'bold' : 'normal' }}>
                    {entity.last_seen ? entity.last_seen.slice(0, 10) : '—'}
                </Typography>
            </TableCell>
        </TableRow>
    );
};

const EntitiesIndexPage: React.FC = () => {
    const { authenticatedFetcher } = useApi();
    // Deep-link support: chips elsewhere in the app link to /entities?q=<name>
    const [searchParams] = useSearchParams();
    const initialQuery = searchParams.get('q') || '';
    // Deep-link support: /entities?sort=last_seen opens the "recent activity" view
    const initialSort: SortKey = searchParams.get('sort') === 'last_seen' ? 'last_seen' : 'report_count';
    const [entityClass, setEntityClass] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<SortKey>(initialSort);
    const [pages, setPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState(initialQuery);
    const [activeSearch, setActiveSearch] = useState(initialQuery);

    React.useEffect(() => {
        const q = searchParams.get('q') || '';
        setSearchTerm(q);
        setActiveSearch(q);
    }, [searchParams]);

    // Index listing (when not searching)
    const indexParams = new URLSearchParams({ limit: String(PAGE_SIZE * pages), offset: '0', sort: sortBy });
    if (entityClass) indexParams.set('entity_class', entityClass);
    const { data: indexData, error: indexError, isLoading: indexLoading } = useSWR<EntityIndexResponse>(
        activeSearch ? null : `/api/kg/entities?${indexParams.toString()}`,
        authenticatedFetcher,
        { revalidateOnFocus: false }
    );

    // Search (name/alias, uses the KG full-text search)
    const kgSearchParams = new URLSearchParams({ query: activeSearch, limit: '50' });
    if (entityClass) kgSearchParams.set('entity_type', entityClass);
    const { data: searchData, error: searchError, isLoading: searchLoading } = useSWR<SearchResponse>(
        activeSearch ? `/api/kg/search?${kgSearchParams.toString()}` : null,
        authenticatedFetcher,
        { revalidateOnFocus: false }
    );

    const isLoading = activeSearch ? searchLoading : indexLoading;
    const error = activeSearch ? searchError : indexError;
    const items: EntityIndexItem[] = activeSearch
        ? (searchData?.results || []).map((r: any) => ({ ...r, last_seen: r.last_seen ?? null }))
        : indexData?.items || [];
    const total = activeSearch ? items.length : indexData?.total || 0;
    const canLoadMore = !activeSearch && items.length < total;

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setActiveSearch(searchTerm.trim());
    };

    const clearSearch = () => {
        setSearchTerm('');
        setActiveSearch('');
    };

    const handleSort = (key: SortKey) => {
        setSortBy(key);
        setPages(1);
    };

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
            <Container maxWidth="lg">
                <Fade in timeout={400}>
                    <HeroSection>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                            <TravelExploreIcon sx={{ fontSize: 44 }} />
                            <Typography variant="h3" component="h1" fontWeight="bold">
                                Threat Entities
                            </Typography>
                        </Stack>
                        <Typography variant="h6" sx={{ opacity: 0.95, maxWidth: '800px' }}>
                            Threat actors, malware families, tools and campaigns tracked across the entire
                            report corpus — aggregated by the Knowledge Graph.
                        </Typography>
                    </HeroSection>
                </Fade>

                <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: `1px solid ${alpha(cyber.primary, 0.2)}` }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                        <Box component="form" onSubmit={handleSearch} sx={{ flex: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name or alias (e.g. Lazarus, Fancy Bear, Cobalt Strike)…"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: activeSearch ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={clearSearch}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                            />
                        </Box>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={entityClass}
                            onChange={(_, v) => { setEntityClass(v); setPages(1); }}
                        >
                            <ToggleButton value="" selected={entityClass === null} onClick={() => { setEntityClass(null); setPages(1); }} sx={{ textTransform: 'none', px: 1.5 }}>
                                All
                            </ToggleButton>
                            {CLASSES.map((c) => (
                                <ToggleButton key={c} value={c} sx={{ textTransform: 'none', px: 1.5 }}>
                                    {c}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Stack>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                        Could not load entities. The Knowledge Graph may be unavailable.
                    </Alert>
                )}

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && !error && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {activeSearch
                                ? `${items.length} result${items.length === 1 ? '' : 's'} for "${activeSearch}"`
                                : `${total} entities${entityClass ? ` of class ${entityClass}` : ''} (seen in 2+ reports) · sorted by ${sortBy === 'last_seen' ? 'most recent evidence' : 'report count'}`}
                        </Typography>
                        <TableContainer component={Paper} sx={{ borderRadius: 2, border: `1px solid ${alpha(cyber.primary, 0.2)}` }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 700, bgcolor: alpha(cyber.primary, 0.06) } }}>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Aliases</TableCell>
                                        <TableCell align="right" sortDirection={!activeSearch && sortBy === 'report_count' ? 'desc' : false}>
                                            {activeSearch ? 'Reports' : (
                                                <TableSortLabel
                                                    active={sortBy === 'report_count'}
                                                    direction="desc"
                                                    onClick={() => handleSort('report_count')}
                                                >
                                                    Reports
                                                </TableSortLabel>
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sortDirection={!activeSearch && sortBy === 'last_seen' ? 'desc' : false}>
                                            {activeSearch ? 'Last Seen' : (
                                                <TableSortLabel
                                                    active={sortBy === 'last_seen'}
                                                    direction="desc"
                                                    onClick={() => handleSort('last_seen')}
                                                >
                                                    Last Seen
                                                </TableSortLabel>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((e) => <EntityRow key={e.id} entity={e} sortBy={sortBy} />)}
                                    {items.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Box sx={{ py: 3, textAlign: 'center' }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {activeSearch
                                                            ? `"${activeSearch}" is not tracked as a canonical entity in the Knowledge Graph yet.`
                                                            : 'No entities found.'}
                                                    </Typography>
                                                    {activeSearch && (
                                                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                                                            Names from CVE context are free-text and may not match a graph entity —
                                                            try a shorter fragment or a known alias, or search the name in IOC/CVE Search.
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {canLoadMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button onClick={() => setPages((p) => p + 1)} sx={{ textTransform: 'none' }}>
                                    Load more ({items.length} of {total})
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Container>
        </Box>
    );
};

export default EntitiesIndexPage;
